/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * One customer-portal application in UI v2 (module RE_CUSTOMER_PORTAL).
 *
 * GET /re/applications/{id} gives the answers, the documents checklist, the
 * whole message thread (customer-visible and internal), the timeline and the
 * moves the status rules allow from here. Every action posts to the backend
 * and shows what it returns; nothing changes on this page without the
 * agent pressing a button:
 * - status moves (POST .../status), with a reason when asking for
 *   information or rejecting, which the customer sees;
 * - messages to the applicant or internal notes (POST .../messages);
 * - accept or reject a document (POST .../documents/{id}/review);
 * - Convert opens the classic Booking wizard or the New lease form filled
 *   from the application; the agent saves it there and that form links the
 *   record back (POST .../link).
 */

import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Skeleton from "@mui/material/Skeleton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ArrowRightLeft, Check, FileText, Lock, Mail, Phone, X } from "lucide-react";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageHeader from "components/v2/PageHeader";
import StagePath from "components/v2/StagePath";
import KeyFieldStrip from "components/v2/KeyFieldStrip";
import UserLink from "components/v2/UserLink";
import SegmentedControl from "components/v2/SegmentedControl";
import { usePermission } from "auth/usePermission";
import { v2 as t } from "styles/tokens/v2";
import { errorMessage, formatDate, formatDateTime, formatMoney, humanize, useReApi, useReGet } from "../../screens/api";
import useReVariant from "../../useReVariant";
import { isAustralia } from "../../variant";
import { Panel, Pill } from "../parts";
import { listingName } from "./ApplicationsV2";
import { APPLICATION_STAGES, STATUS_LABEL, STATUS_TONE, actionsFor, conversionPath, stageFor, typeLabel } from "./logic";

const DOC_STATE = {
  MISSING: ["Not uploaded", "neutral"], UPLOADED: ["To review", "info"], ACCEPTED: ["Accepted", "success"], REJECTED: ["Rejected", "danger"],
};

// Event kinds written by backend real_estate/portal/service.py (_event).
const EVENT_WORDS = {
  SUBMITTED: "Application sent",
  NOTE: "Internal note",
  DOCUMENT: "Document uploaded",
  DOCUMENT_REVIEW: "Document reviewed",
  CONTACT_LINKED: "Contact linked",
};

function eventTitle(e) {
  if (e.kind === "STATUS") return STATUS_LABEL[e.to_status] || humanize(e.to_status);
  if (e.kind === "MESSAGE") return e.actor_type === "CUSTOMER" ? "Applicant sent a message" : "Message to the applicant";
  return EVENT_WORDS[e.kind] || humanize(e.kind);
}

function answerValue(a, currency) {
  if (a.value === null || a.value === undefined || a.value === "") return "—";
  if (a.kind === "money") return formatMoney(a.value, currency, { decimals: 0 });
  if (a.kind === "date") return formatDate(a.value);
  return String(a.value);
}

/** Asks for the text the backend needs: the reason the applicant sees, or a document rejection note. */
function ReasonDialog({ open, title, help, label, confirm, danger, onClose, onConfirm }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const close = () => { setText(""); setErr(null); onClose(); };
  return (
    <Dialog open={open} onClose={busy ? undefined : close} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {help && <Typography sx={{ fontSize: 14, color: t.text2, mb: 2 }}>{help}</Typography>}
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <TextField autoFocus fullWidth multiline minRows={3} label={label} value={text} onChange={(e) => setText(e.target.value)} inputProps={{ maxLength: 4000 }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color={danger ? "error" : "primary"}
          disabled={busy || !text.trim()}
          sx={{ color: "#FFFFFF !important" }}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            const problem = await onConfirm(text.trim());
            setBusy(false);
            if (problem) setErr(problem);
            else close();
          }}
        >
          {busy ? "Saving…" : confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Documents({ app, canUpdate, onOpenFile, onReview }) {
  if (!app.checklist.length) return <Typography sx={{ fontSize: 14, color: t.text3 }}>No documents are asked for on this type of application.</Typography>;
  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      {app.checklist.map((item) => {
        const [label, tone] = item.state === "MISSING" && !item.required ? ["Optional", "neutral"] : DOC_STATE[item.state] || [item.state, "neutral"];
        return (
          <Box key={item.key} sx={{ border: `1px solid ${t.border}`, borderRadius: "8px", p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center" }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: t.text }}>{`${item.label}${item.required ? "" : " (optional)"}`}</Typography>
              <Pill tone={tone}>{label}</Pill>
            </Box>
            {item.documents.map((d) => (
              <Box key={d.id} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mt: 1.25, pt: 1.25, borderTop: `1px dashed ${t.border}` }}>
                <FileText size={16} color={t.text3} aria-hidden />
                <Box
                  component="button"
                  type="button"
                  onClick={() => onOpenFile(d)}
                  sx={{ p: 0, border: 0, bgcolor: "transparent", cursor: "pointer", font: "inherit", fontSize: 14, color: t.link, "&:hover": { textDecoration: "underline" } }}
                >
                  {d.file_name}
                </Box>
                <Typography sx={{ fontSize: 13, color: t.text3 }}>{`${formatDateTime(d.created_at)}${d.size_bytes ? ` · ${Math.max(1, Math.round(d.size_bytes / 1024))} KB` : ""}`}</Typography>
                <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                  {d.status === "UPLOADED" && canUpdate ? (
                    <>
                      <Button size="small" variant="outlined" startIcon={<Check size={15} />} onClick={() => onReview(d, "ACCEPTED")}>Accept</Button>
                      <Button size="small" variant="outlined" color="error" startIcon={<X size={15} />} onClick={() => onReview(d, "REJECTED")}>Reject</Button>
                    </>
                  ) : (
                    <Pill tone={DOC_STATE[d.status]?.[1] || "neutral"}>{d.status === "UPLOADED" ? "To review" : humanize(d.status)}</Pill>
                  )}
                </Box>
                {d.review_note && <Typography sx={{ width: "100%", fontSize: 13, color: t.text2 }}>{`Note to the applicant: ${d.review_note}`}</Typography>}
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}

function Messages({ app, canUpdate, onSend }) {
  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState("CUSTOMER");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const closed = ["REJECTED", "WITHDRAWN"].includes(app.status);
  const audience = closed ? "INTERNAL" : visibility;
  return (
    <Box>
      {app.messages.length === 0 && <Typography sx={{ fontSize: 14, color: t.text3, mb: 2 }}>No messages yet.</Typography>}
      <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 1.25, mb: 2.5 }}>
        {app.messages.map((m) => {
          const internal = m.visibility === "INTERNAL";
          const fromApplicant = m.author_type === "CUSTOMER";
          return (
            <Box
              component="li"
              key={m.id}
              sx={{
                maxWidth: "88%", justifySelf: fromApplicant ? "start" : "end", p: 1.5, borderRadius: "10px",
                border: `1px ${internal ? "dashed" : "solid"} ${internal ? "#D9B26A" : t.border}`,
                bgcolor: internal ? "#FFF8EB" : fromApplicant ? t.surface : t.surface2,
              }}
            >
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: internal ? "#8A4B08" : t.text3, display: "flex", alignItems: "center", gap: 0.5 }}>
                {internal && <Lock size={12} aria-hidden />}
                {`${fromApplicant ? app.applicant?.name || "Applicant" : m.author || "Staff"}${internal ? " · internal note" : ""} · ${formatDateTime(m.created_at)}`}
              </Typography>
              <Typography sx={{ fontSize: 14.5, color: t.text, whiteSpace: "pre-line", mt: 0.5 }}>{m.body}</Typography>
            </Box>
          );
        })}
      </Box>
      {canUpdate && (
        <Box
          component="form"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setErr(null);
            const problem = await onSend(text.trim(), audience);
            setBusy(false);
            if (problem) setErr(problem);
            else setText("");
          }}
          sx={{ display: "grid", gap: 1.5 }}
        >
          {!closed && (
            <SegmentedControl
              label="Who sees it"
              value={visibility}
              onChange={setVisibility}
              options={[{ value: "CUSTOMER", label: "Message to applicant" }, { value: "INTERNAL", label: "Internal note" }]}
            />
          )}
          {err && <Alert severity="error">{err}</Alert>}
          <TextField
            multiline
            minRows={3}
            label={audience === "INTERNAL" ? "Internal note (the applicant never sees it)" : "Message (emailed to the applicant)"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            inputProps={{ maxLength: 4000 }}
          />
          <Box>
            <Button type="submit" variant="contained" disabled={busy || !text.trim()} sx={{ color: "#FFFFFF !important" }}>
              {busy ? "Sending…" : audience === "INTERNAL" ? "Add note" : "Send message"}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function ApplicationRecordV2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pack = useReVariant();
  const au = isAustralia(pack);
  const { api } = useReApi();
  // Set by the Booking wizard or New lease form when the record saved but the link back failed.
  const [unlinked, setUnlinked] = useState(location.state?.linkError ? location.state : null);
  const { canEdit } = usePermission("re_applications");
  const bookings = usePermission("re_bookings");
  const leases = usePermission("re_leases");
  const { data: app, loading, error, reload, setData } = useReGet(`/re/applications/${id}`);
  const [tab, setTab] = useState("answers");
  const [dialog, setDialog] = useState(null);
  const [notice, setNotice] = useState(null);

  if (loading && !app) {
    return <DashboardLayout><DashboardNavbar /><Box sx={{ pt: 3 }}><Skeleton variant="rounded" height={260} /></Box></DashboardLayout>;
  }
  if (!app) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box sx={{ pt: 3 }}>
          <Alert severity="error" action={<Button onClick={reload}>Retry</Button>}>{errorMessage(error, "This application could not be loaded.")}</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  /** Posts an action; the backend answers with the whole record, which replaces ours. Returns an error message or null. */
  const act = async (path, body, done) => {
    try {
      const res = await api.post(`/re/applications/${app.id}${path}`, body);
      setData(res.data);
      setNotice(done ? { severity: "success", text: done } : null);
      return null;
    } catch (e) {
      return errorMessage(e, "That didn't work. Please try again.");
    }
  };

  const move = async (action) => {
    if (action.needsReason) { setDialog({ kind: "move", action }); return; }
    const problem = await act("/status", { to: action.to }, `${STATUS_LABEL[action.to]}. The applicant has been emailed.`);
    if (problem) setNotice({ severity: "error", text: problem });
  };

  const openFile = async (doc) => {
    const tabRef = window.open("", "_blank");
    if (tabRef) tabRef.opener = null;
    try {
      const res = await api.get(`/re/applications/${app.id}/documents/${doc.id}/download`);
      if (tabRef) tabRef.location.href = res.data.url;
      else window.location.assign(res.data.url);
    } catch (e) {
      if (tabRef) tabRef.close();
      setNotice({ severity: "error", text: errorMessage(e, "The file could not be opened.") });
    }
  };

  const review = async (doc, status) => {
    if (status === "REJECTED") { setDialog({ kind: "reject-doc", doc }); return; }
    const problem = await act(`/documents/${doc.id}/review`, { status: "ACCEPTED" }, `${doc.file_name} accepted.`);
    if (problem) setNotice({ severity: "error", text: problem });
  };

  const actions = actionsFor(app.allowed_moves, { canUpdate: canEdit });
  const canConvert = canEdit && app.status === "APPROVED" && (app.type === "PURCHASE" ? bookings.canCreate : leases.canCreate);
  const stage = stageFor(app.status);
  const listing = app.listing;
  const currency = listing?.currency || pack?.currency;
  const docsToReview = app.checklist.reduce((n, c) => n + c.documents.filter((d) => d.status === "UPLOADED").length, 0);
  const applicantName = app.applicant?.name || app.applicant_name || app.applicant?.email || "Applicant";
  const recordLink = app.booking_id ? `/real-estate/bookings/${app.booking_id}` : app.lease_id ? `/real-estate/leases/${app.lease_id}` : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ pt: 3, pb: 6 }}>
        <PageHeader
          title={applicantName}
          summary={`${typeLabel(app.type, au)} ${app.reference || ""} · sent ${formatDate(app.submitted_at)}`}
          actions={(
            <>
              {actions.filter((a) => !a.primary).map((a) => (
                <Button key={a.to} variant="outlined" color={a.danger ? "error" : "primary"} onClick={() => move(a)}>{a.label}</Button>
              ))}
              {actions.filter((a) => a.primary).map((a) => (
                <Button key={a.to} variant="contained" onClick={() => move(a)} sx={{ color: "#FFFFFF !important" }}>{a.label}</Button>
              ))}
              {canConvert && (
                <Button variant="contained" startIcon={<ArrowRightLeft size={16} />} onClick={() => navigate(conversionPath(app))} sx={{ color: "#FFFFFF !important" }}>
                  {app.type === "PURCHASE" ? "Create booking" : "Create lease"}
                </Button>
              )}
              {recordLink && <Button variant="outlined" onClick={() => navigate(recordLink)}>{app.booking_id ? "Open booking" : "Open lease"}</Button>}
            </>
          )}
        />

        {notice && <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}
        {unlinked && app.status === "APPROVED" && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={canEdit ? (
              <Button
                color="inherit"
                size="small"
                onClick={async () => {
                  const body = unlinked.bookingId ? { booking_id: unlinked.bookingId } : { lease_id: unlinked.leaseId };
                  const problem = await act("/link", body, "Linked. The application is converted and the applicant has been emailed.");
                  setUnlinked(problem ? { ...unlinked, linkError: problem } : null);
                }}
              >
                Link again
              </Button>
            ) : null}
          >
            {`The ${unlinked.bookingId ? "booking" : "lease"} was saved, but this application wasn't marked converted: ${unlinked.linkError}`}
          </Alert>
        )}
        {app.status === "APPROVED" && canConvert && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {`Approved. ${app.type === "PURCHASE" ? "Create booking" : "Create lease"} opens the form filled in from this application; the application is marked converted once you save it there.`}
          </Alert>
        )}

        <Panel sx={{ mb: 2.5 }}>
          <StagePath
            steps={APPLICATION_STAGES.map((s) => STATUS_LABEL[s])}
            activeStep={stage.activeStep}
            finalState={stage.finalState}
          />
          {app.status === "INFO_REQUESTED" && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>{`Waiting on the applicant: ${app.status_reason || "information requested."}`}</Alert>
          )}
          {app.status === "REJECTED" && app.status_reason && <Alert severity="error" sx={{ mt: 1.5 }}>{`Rejected: ${app.status_reason}`}</Alert>}
          {app.status === "WITHDRAWN" && <Alert severity="info" sx={{ mt: 1.5 }}>The applicant withdrew this application.</Alert>}
          <KeyFieldStrip
            items={[
              { label: "Status", value: <Pill tone={STATUS_TONE[app.status]}>{STATUS_LABEL[app.status] || app.status}</Pill> },
              { label: "Listing", value: listingName(listing) },
              { label: app.type === "RENTAL" ? "Rent" : "Price", value: listing?.price != null ? formatMoney(listing.price, currency, { decimals: 0 }) : "—" },
              { label: "Agent", value: app.assigned_to_name ? <UserLink userId={app.assigned_to} name={app.assigned_to_name} tone="link" /> : "Unassigned" },
              { label: "Documents to review", value: String(docsToReview) },
            ]}
          />
        </Panel>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(280px, 1fr)" }, gap: 2.5 }}>
          <Panel sx={{ p: 0 }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ px: 2, borderBottom: `1px solid ${t.border}` }}>
              <Tab value="answers" label="Answers" />
              <Tab value="documents" label={`Documents${docsToReview ? ` (${docsToReview} to review)` : ""}`} />
              <Tab value="messages" label={`Messages (${app.messages.length})`} />
              <Tab value="timeline" label="Timeline" />
            </Tabs>
            <Box sx={{ p: 2.5 }}>
              {tab === "answers" && (
                app.answers.length ? (
                  <Box component="dl" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, columnGap: 3, rowGap: 2, m: 0 }}>
                    {app.answers.map((a) => (
                      <Box key={a.key} sx={{ borderBottom: `1px solid ${t.border}`, pb: 1.25 }}>
                        <Typography component="dt" sx={{ fontSize: 13, color: t.text3 }}>{a.label}</Typography>
                        <Typography component="dd" sx={{ m: 0, fontSize: 15, color: t.text, fontWeight: 500, whiteSpace: "pre-line" }}>{answerValue(a, currency)}</Typography>
                      </Box>
                    ))}
                  </Box>
                ) : <Typography sx={{ fontSize: 14, color: t.text3 }}>No answers.</Typography>
              )}
              {tab === "documents" && <Documents app={app} canUpdate={canEdit} onOpenFile={openFile} onReview={review} />}
              {tab === "messages" && (
                <Messages
                  app={app}
                  canUpdate={canEdit}
                  onSend={(body, visibility) => act("/messages", { body, visibility }, visibility === "INTERNAL" ? "Note added." : "Message sent. The applicant has been emailed.")}
                />
              )}
              {tab === "timeline" && (
                <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 1.75 }}>
                  {[...app.timeline].reverse().map((e, i) => (
                    <Box component="li" key={`${e.at}-${i}`} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: t.text }}>{eventTitle(e)}</Typography>
                        <Typography sx={{ fontSize: 13, color: t.text3 }}>
                          {[e.actor_type === "CUSTOMER" ? "Applicant" : e.actor || (e.actor_type === "SYSTEM" ? "System" : null), e.note].filter(Boolean).join(" · ")}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 12.5, color: t.text3, whiteSpace: "nowrap" }}>{formatDateTime(e.at)}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Panel>

          <Box sx={{ display: "grid", gap: 2.5, alignContent: "start" }}>
            <Panel title="Applicant">
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: t.text }}>{applicantName}</Typography>
              {app.applicant?.email && (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
                  <Mail size={15} color={t.text3} aria-hidden />
                  <Box component="a" href={`mailto:${app.applicant.email}`} sx={{ fontSize: 14, color: t.link }}>{app.applicant.email}</Box>
                </Box>
              )}
              {app.applicant?.phone && (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.75 }}>
                  <Phone size={15} color={t.text3} aria-hidden />
                  <Box component="a" href={`tel:${app.applicant.phone}`} sx={{ fontSize: 14, color: t.link }}>{app.applicant.phone}</Box>
                </Box>
              )}
              {app.contact_id && (
                <Button size="small" sx={{ mt: 1.5, px: 0 }} onClick={() => navigate(`/contacts/${app.contact_id}`)}>Open contact</Button>
              )}
            </Panel>
            {listing && (
              <Panel title="Listing">
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: t.text }}>{listingName(listing)}</Typography>
                <Typography sx={{ fontSize: 13.5, color: t.text3 }}>
                  {[listing.project, listing.suburb, humanize(listing.status)].filter(Boolean).join(" · ")}
                </Typography>
                <Button size="small" sx={{ mt: 1.5, px: 0 }} onClick={() => navigate(`/real-estate/listings/${listing.id}`)}>Open listing</Button>
              </Panel>
            )}
          </Box>
        </Box>
      </Box>

      <ReasonDialog
        open={dialog?.kind === "move"}
        title={dialog?.action?.to === "REJECTED" ? "Reject this application" : "Ask the applicant for information"}
        help={dialog?.action?.to === "REJECTED"
          ? "The applicant is emailed and sees this reason in their account."
          : "The applicant is emailed this message and can reply or upload documents from their account."}
        label={dialog?.action?.to === "REJECTED" ? "Reason" : "What do you need?"}
        confirm={dialog?.action?.to === "REJECTED" ? "Reject" : "Send request"}
        danger={dialog?.action?.to === "REJECTED"}
        onClose={() => setDialog(null)}
        onConfirm={(reason) => act("/status", { to: dialog.action.to, reason }, `${STATUS_LABEL[dialog.action.to]}. The applicant has been emailed.`)}
      />
      <ReasonDialog
        open={dialog?.kind === "reject-doc"}
        title={`Reject ${dialog?.doc?.file_name || "document"}`}
        help="The applicant sees this note next to the file and can upload a new copy."
        label="What's wrong with it?"
        confirm="Reject document"
        danger
        onClose={() => setDialog(null)}
        onConfirm={(note) => act(`/documents/${dialog.doc.id}/review`, { status: "REJECTED", note }, "Document rejected.")}
      />
    </DashboardLayout>
  );
}
